import {
  CheckCircleOutlined,
  LockOutlined,
  QrcodeOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { Alert, Button, Input, Modal, Space, Steps, Typography, message } from "antd";
import type { StepsProps } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../services/api";

const { Title, Paragraph, Text } = Typography;

interface TOTPSetupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SetupStep {
  title: string;
  icon: JSX.Element;
  content: JSX.Element;
}

export default function TOTPSetupModal({
  open,
  onClose,
  onSuccess,
}: TOTPSetupModalProps): JSX.Element {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [secret, setSecret] = useState<string>("");
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState<string>("");
  const [setupLoading, setSetupLoading] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      fetchTOTPSetup();
    } else {
      // Reset state when modal closes
      setCurrentStep(0);
      setSecret("");
      setQrCodeUrl("");
      setVerifyCode("");
    }
  }, [open]);

  const fetchTOTPSetup = async (): Promise<void> => {
    try {
      setSetupLoading(true);
      const response = await api.setupTOTP();

      setSecret(response.secret);
      setQrCodeUrl(response.qr_code);
    } catch (error) {
      console.error("Failed to setup TOTP:", error);
      message.error(t("totpSetup.setupFailed"));
      onClose();
    } finally {
      setSetupLoading(false);
    }
  };

  const handleVerifyAndEnable = async (): Promise<void> => {
    if (!verifyCode || verifyCode.length !== 6) {
      message.error(t("totpSetup.invalidCode"));
      return;
    }

    try {
      setLoading(true);

      await api.enableTOTP(verifyCode);

      message.success(t("totpSetup.enableSuccess"));
      setCurrentStep(2);
      onSuccess?.();

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Failed to enable TOTP:", error);
      message.error(error instanceof Error ? error.message : t("totpSetup.invalidCodeRetry"));
    } finally {
      setLoading(false);
    }
  };

  const steps: SetupStep[] = [
    {
      title: t("totpSetup.steps.scan"),
      icon: <QrcodeOutlined />,
      content: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert
            message={t("totpSetup.installApp.message")}
            description={t("totpSetup.installApp.description")}
            type="info"
            showIcon
          />

          {setupLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>{t("totpSetup.loadingText")}</div>
          ) : (
            <>
              <div style={{ textAlign: "center" }}>
                <Title level={4}>{t("totpSetup.scanTitle")}</Title>
                {qrCodeUrl && (
                  <img
                    src={`data:image/png;base64,${qrCodeUrl.replace("data:image/png;base64,", "")}`}
                    alt="TOTP QR Code"
                    style={{
                      maxWidth: "300px",
                      border: "1px solid #d9d9d9",
                      borderRadius: "8px",
                      padding: "16px",
                      backgroundColor: "white",
                    }}
                  />
                )}
              </div>

              <Alert
                message={t("totpSetup.manualEntry.message")}
                description={
                  <div>
                    {t("totpSetup.manualEntry.description")}
                    <br />
                    <Text code copyable style={{ fontSize: "16px", marginTop: "8px" }}>
                      {secret}
                    </Text>
                  </div>
                }
                type="warning"
              />
            </>
          )}
        </Space>
      ),
    },
    {
      title: t("totpSetup.steps.verify"),
      icon: <LockOutlined />,
      content: (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Alert
            message={t("totpSetup.enterCode.message")}
            description={t("totpSetup.enterCode.description")}
            type="info"
            showIcon
          />

          <div>
            <Title level={4}>{t("totpSetup.enterCodeTitle")}</Title>
            <Input
              size="large"
              placeholder="000000"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
              style={{ fontSize: "24px", textAlign: "center", letterSpacing: "8px" }}
              autoFocus
              onPressEnter={handleVerifyAndEnable}
            />
          </div>

          <Button
            type="primary"
            size="large"
            block
            onClick={handleVerifyAndEnable}
            loading={loading}
            disabled={verifyCode.length !== 6}
            icon={<SafetyOutlined />}
          >
            {t("totpSetup.verifyAndEnable")}
          </Button>
        </Space>
      ),
    },
    {
      title: t("totpSetup.steps.complete"),
      icon: <CheckCircleOutlined />,
      content: (
        <Space direction="vertical" size="large" style={{ width: "100%", textAlign: "center" }}>
          <CheckCircleOutlined style={{ fontSize: "64px", color: "#52c41a" }} />
          <Title level={3}>{t("totpSetup.completeTitle")}</Title>
          <Paragraph>{t("totpSetup.completeBody")}</Paragraph>
        </Space>
      ),
    },
  ];

  const stepsItems: StepsProps["items"] = steps.map((step) => ({
    key: step.title,
    title: step.title,
    icon: step.icon,
  }));

  return (
    <Modal
      title={t("totpSetup.title")}
      open={open}
      onCancel={onClose}
      width={600}
      footer={
        currentStep < 2
          ? [
              <Button key="cancel" onClick={onClose}>
                {t("totpSetup.cancel")}
              </Button>,
              currentStep === 0 && (
                <Button
                  key="next"
                  type="primary"
                  onClick={() => setCurrentStep(1)}
                  disabled={setupLoading}
                >
                  {t("totpSetup.nextVerify")}
                </Button>
              ),
            ]
          : null
      }
    >
      <Steps current={currentStep} style={{ marginBottom: "32px" }} items={stepsItems} />

      <div style={{ minHeight: "300px" }}>{steps[currentStep].content}</div>
    </Modal>
  );
}
